import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Breadcrumbs,
  Link,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  Snackbar,
  Alert,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import states from '../../../../../utils/states.json';
import { adminMenuItems } from '../../../../../config/layout';
import { createEntity, updateEntity } from '../../../../../api/entityApi';
import { useErrorModal } from '../../../../../hooks/useErrorModal';
import PermissionGuard from '../../../../../components/PermissionGuard';

const AddLegalEntity = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const locationState = useLocation();
  const isEdit = !!id;

  const orgManagementItem = adminMenuItems.find((item) => item.id === 'org-management');

  const currentBreadcrumbs = useMemo(() => {
    const currentnavItem = orgManagementItem?.subItems?.find(
      (item) => item.id === 'org-structure'
    );
    return currentnavItem?.breadcrumbs || [];
  }, [orgManagementItem]);

  const [formData, setFormData] = useState({
    country: '',
    companyName: '',
    legalEntityName: '',
    companyId: '',
    incorporationDate: '',
    businessType: '',
    industryType: '',
    websiteUrl: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const { showError, ErrorModal } = useErrorModal();

  useEffect(() => {
    if (isEdit) {
      const entityData = locationState.state?.entityData;
      if (entityData) {
        setFormData({
          country: entityData.country || '',
          companyName: entityData.name || '',
          legalEntityName: entityData.legalEntityName || '',
          companyId: entityData.legalInfo?.cin || '',
          incorporationDate: entityData.legalInfo?.incorporationDate ? new Date(entityData.legalInfo.incorporationDate).toISOString().split('T')[0] : '',
          businessType: entityData.businessType || '',
          industryType: entityData.industry || '',
          websiteUrl: entityData.website || '',
          address1: entityData.address || '',
          address2: entityData.address2 || '',
          city: entityData.city || '',
          state: entityData.state || '',
        });
      } else {
        setNotification({
          open: true,
          message: 'No entity data provided. Redirecting...',
          severity: 'error'
        });
        setTimeout(() => navigate('/org-management/org-structure'), 2000);
      }
    }
  }, [isEdit, locationState.state, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleCancel = () => {
    navigate('/org-management/org-structure');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.country.trim()) newErrors.country = 'Country is required';
    if (!formData.companyName.trim()) newErrors.companyName = 'Company name is required';
    if (!formData.legalEntityName.trim()) newErrors.legalEntityName = 'Legal entity name is required';
    if (!formData.companyId.trim()) newErrors.companyId = 'Company ID is required';
    if (!formData.incorporationDate.trim()) newErrors.incorporationDate = 'Incorporation date is required';
    if (!formData.businessType.trim()) newErrors.businessType = 'Business type is required';
    if (!formData.industryType.trim()) newErrors.industryType = 'Industry type is required';
    if (!formData.address1.trim()) newErrors.address1 = 'Address 1 is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const entityData = {
        name: formData.companyName,
        legalEntityName: formData.legalEntityName,
        country: formData.country,
        city: formData.city,
        state: formData.state,
        address: formData.address1,
        address2: formData.address2,
        website: formData.websiteUrl,
        industry: formData.industryType,
        businessType: formData.businessType,
        legalInfo: {
          cin: formData.companyId,
          incorporationDate: formData.incorporationDate,
        }
      };

      let result;
      if (isEdit) {
        result = await updateEntity(id, entityData);
      } else {
        result = await createEntity(entityData);
      }

      if (result.success) {
        setNotification({
          open: true,
          message: `Legal entity ${isEdit ? 'updated' : 'created'} successfully!`,
          severity: 'success',
        });

        setTimeout(() => {
          navigate('/org-management/org-structure');
        }, 1500);
      } else {
        setNotification({
          open: true,
          message: result.message || `Failed to ${isEdit ? 'update' : 'create'} legal entity`,
          severity: 'error',
        });
      }
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  return (
    <Container maxWidth="xl" sx={{ p: 3 }}>
      <Breadcrumbs
        separator={<NavigateNextIcon sx={{ fontSize: '1rem' }} />}
        sx={{
          marginBottom: 3,
          marginTop: 2,
          '& .MuiBreadcrumbs-li': {
            fontSize: '0.875rem',
          },
        }}
      >
        {currentBreadcrumbs.map((crumb) => (
          <Link
            key={crumb.path}
            onClick={() => navigate(crumb.path)}
            sx={{
              cursor: 'pointer',
              color: '#7522D1',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {crumb.label}
          </Link>
        ))}
        <Typography sx={{ color: '#030007', fontSize: '0.875rem' }}>
          {isEdit ? 'Edit Legal Entity' : 'Add Legal Entity'}
        </Typography>
      </Breadcrumbs>

      <Box sx={{ marginBottom: 4 }}>
        <Box
          sx={{
            backgroundColor: '#F5F5F5',
            padding: '20px',
            marginBottom: 3,
            borderRadius: 1,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: '#030007',
                marginBottom: 0.5,
                fontSize: '1.1rem',
              }}
            >
              {isEdit ? 'Edit Legal Entity' : 'Legal Entities'}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#4F4937',
                fontSize: '0.9rem',
              }}
            >
              {isEdit ? 'Update your legal entity information' : 'Add your team as per required'}
            </Typography>
          </Box>
        </Box>

        <Card
          sx={{
            padding: 3,
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            borderRadius: 1,
          }}
        >
          <Box sx={{ marginBottom: 4 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#030007',
                marginBottom: 1,
                fontSize: '1rem',
              }}
            >
              Entity Details
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#4F4937',
                marginBottom: 3,
                fontSize: '0.9rem',
              }}
            >
              {isEdit ? 'Update' : 'Enter'} the basic information for your legal entity.
            </Typography>

            <Box sx={{ marginBottom: 3 }}>
              <FormControl fullWidth error={!!errors.country}>
                <InputLabel>Country *</InputLabel>
                <Select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  label="Country *"
                >
                  <MenuItem value="">Select Country</MenuItem>
                  <MenuItem value="usa">United States</MenuItem>
                  <MenuItem value="india">India</MenuItem>
                  <MenuItem value="uk">United Kingdom</MenuItem>
                </Select>
                {errors.country && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                    {errors.country}
                  </Typography>
                )}
              </FormControl>
            </Box>
          </Box>

          <Box sx={{ marginBottom: 4 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#030007',
                marginBottom: 1,
                fontSize: '1rem',
              }}
            >
              Legal Information
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#4F4937',
                marginBottom: 3,
                fontSize: '0.9rem',
              }}
            >
              Provide the official company details including registration numbers and tax identification.
            </Typography>

            <Grid container spacing={2} sx={{ marginBottom: 3 }}>
              <Grid item xs={12} md={6} size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Company Name *"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  placeholder="Enter here"
                  variant="outlined"
                  error={!!errors.companyName}
                  helperText={errors.companyName}
                />
              </Grid>

              <Grid item xs={12} md={6} size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Legal Entity Name *"
                  name="legalEntityName"
                  value={formData.legalEntityName}
                  onChange={handleChange}
                  placeholder="Enter Name"
                  variant="outlined"
                  error={!!errors.legalEntityName}
                  helperText={errors.legalEntityName}
                />
              </Grid>

              <Grid item xs={12} md={6} size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Company Identification Number *"
                  name="companyId"
                  value={formData.companyId}
                  onChange={handleChange}
                  placeholder="Enter Name"
                  variant="outlined"
                  error={!!errors.companyId}
                  helperText={errors.companyId}
                />
              </Grid>

              <Grid item xs={12} md={6} size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Incorporation Date *"
                  name="incorporationDate"
                  type="date"
                  value={formData.incorporationDate}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  variant="outlined"
                  error={!!errors.incorporationDate}
                  helperText={errors.incorporationDate}
                />
              </Grid>

              <Grid item xs={12} md={6} size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth error={!!errors.businessType}>
                  <InputLabel>Business Type *</InputLabel>
                  <Select
                    name="businessType"
                    value={formData.businessType}
                    onChange={handleChange}
                    label="Business Type *"
                  >
                    <MenuItem value="">Select Type</MenuItem>
                    <MenuItem value="private">Private Limited</MenuItem>
                    <MenuItem value="public">Public Limited</MenuItem>
                  </Select>
                  {errors.businessType && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
                      {errors.businessType}
                    </Typography>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6} size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Industry Type *"
                  name="industryType"
                  value={formData.industryType}
                  onChange={handleChange}
                  placeholder="e.g. Logistics"
                  variant="outlined"
                  error={!!errors.industryType}
                  helperText={errors.industryType}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Website URL"
                  name="websiteUrl"
                  value={formData.websiteUrl}
                  onChange={handleChange}
                  placeholder="Paste here"
                  variant="outlined"
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{ marginBottom: 4 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: '#030007',
                marginBottom: 3,
                fontSize: '1rem',
              }}
            >
              Address
            </Typography>

            <Grid container spacing={2} sx={{ marginBottom: 3 }}>
              <Grid item xs={12} md={6} size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Address 1 *"
                  name="address1"
                  value={formData.address1}
                  onChange={handleChange}
                  placeholder="Enter here"
                  variant="outlined"
                  error={!!errors.address1}
                  helperText={errors.address1}
                />
              </Grid>

              <Grid item xs={12} md={6} size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="Address 2"
                  name="address2"
                  value={formData.address2}
                  onChange={handleChange}
                  placeholder="Enter Name"
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} md={6} size={{ xs: 12, md: 6 }}>
                <TextField
                  fullWidth
                  label="City *"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Enter Name"
                  variant="outlined"
                  error={!!errors.city}
                  helperText={errors.city}
                />
              </Grid>

              <Grid item xs={12} md={6} size={{ xs: 12, md: 6 }}>
                <Autocomplete
                  fullWidth
                  options={states}
                  value={formData.state || null}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({ ...prev, state: newValue || "" }));
                    if (errors.state) {
                      setErrors(prev => ({ ...prev, state: "" }));
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="State *"
                      placeholder="Select State"
                      error={!!errors.state}
                      helperText={errors.state}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginTop: 4,
              paddingTop: 3,
              borderTop: '1px solid #E8E8E8',
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCancel}
              sx={{
                borderColor: '#030007',
                color: '#030007',
                textTransform: 'none',
                fontWeight: 600,
                paddingX: 3,
                paddingY: 1.2,
                borderRadius: 1,
                '&:hover': {
                  borderColor: '#030007',
                  backgroundColor: 'transparent',
                },
              }}
            >
              Cancel
            </Button>
            <PermissionGuard module="entities" action={isEdit ? "edit" : "create"}>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{
                  backgroundColor: '#7522D1',
                  color: '#FFFFFF',
                  textTransform: 'none',
                  fontWeight: 600,
                  paddingX: 3,
                  paddingY: 1.2,
                  borderRadius: 1,
                  '&:hover': {
                    backgroundColor: '#5E1BA8',
                  },
                }}
              >
                {loading ? (isEdit ? 'Updating...' : 'Adding...') : (isEdit ? 'Update' : 'Add')}
              </Button>
            </PermissionGuard>
          </Box>
        </Card>
      </Box>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      <ErrorModal />
    </Container>
  );
};

export default AddLegalEntity;
